/**
 * Lógica pura del recorrido inmersivo de una unidad (Mati, 2026-11-01): dónde está el sol según la estación y la hora,
 * cómo se pasa de la planta a la escena 3D y a qué ambientes se puede ir. El visor (`components/Inmersivo3D.tsx`)
 * sólo dibuja lo que sale de acá.
 *
 * Escena: la losa de la unidad en y = 0; x igual que en la planta y z = −y, así la fachada de la unidad queda en z = 0
 * y afuera (el balcón, la calle o el pulmón de manzana) hacia +z. Parado adentro mirando la fachada (+z), la derecha es −x.
 */
import type { RectPlanta } from "./api";

export type Estacion = "verano" | "otonio" | "invierno" | "primavera";

export const ESTACIONES: { valor: Estacion; etiqueta: string }[] = [
  { valor: "verano", etiqueta: "Verano (21 de diciembre)" },
  { valor: "otonio", etiqueta: "Otoño (20 de marzo)" },
  { valor: "invierno", etiqueta: "Invierno (21 de junio)" },
  { valor: "primavera", etiqueta: "Primavera (22 de septiembre)" },
];

const DIA: Record<Estacion, [number, number]> = { verano: [11, 21], otonio: [2, 20], invierno: [5, 21], primavera: [8, 22] };
const ANIO = 2026;
const HUSO_ARGENTINA = 3; // UTC−3, sin horario de verano

/** Sin el terreno ubicado: el centro de Buenos Aires. */
export const ARGENTINA = { latitud: -34.6037, longitud: -58.3816 };

/** Hacia dónde puede mirar el frente: los ocho rumbos. */
export const RUMBOS: { valor: number; etiqueta: string }[] = [
  { valor: 0, etiqueta: "Norte" },
  { valor: 45, etiqueta: "Noreste" },
  { valor: 90, etiqueta: "Este" },
  { valor: 135, etiqueta: "Sudeste" },
  { valor: 180, etiqueta: "Sur" },
  { valor: 225, etiqueta: "Sudoeste" },
  { valor: 270, etiqueta: "Oeste" },
  { valor: 315, etiqueta: "Noroeste" },
];

/** El instante de la escena: el día de la estación a esa hora de Argentina (hora con decimales: 13.5 = 13:30). */
export function fechaDeEscena(estacion: Estacion, hora: number): Date {
  const [mes, dia] = DIA[estacion];
  const minutos = Math.round(hora * 60);
  return new Date(Date.UTC(ANIO, mes, dia, HUSO_ARGENTINA, minutos));
}

export function etiquetaHora(hora: number): string {
  const minutos = Math.round(hora * 60);
  return `${Math.floor(minutos / 60)}:${String(minutos % 60).padStart(2, "0")}`;
}

export interface PosicionSol {
  /** Grados desde el norte, en el sentido de las agujas del reloj (90 = este). */
  azimut: number;
  /** Grados sobre el horizonte (negativo: de noche). */
  altura: number;
}

const RAD = Math.PI / 180;

/** Posición del sol (algoritmo de SunCalc, de V. Agafonkin; error de décimas de grado, de sobra para la luz). */
export function posicionSol(fecha: Date, latitud: number, longitud: number): PosicionSol {
  const d = fecha.valueOf() / 86_400_000 - 0.5 + 2_440_588 - 2_451_545;
  const e = RAD * 23.4397;
  const M = RAD * (357.5291 + 0.98560028 * d);
  const C = RAD * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
  const L = M + C + RAD * 102.9372 + Math.PI;
  const dec = Math.asin(Math.sin(e) * Math.sin(L));
  const ra = Math.atan2(Math.sin(L) * Math.cos(e), Math.cos(L));
  const phi = RAD * latitud;
  const H = RAD * (280.16 + 360.9856235 * d) + RAD * longitud - ra;
  const azSur = Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi));
  const altura = Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H));
  return { azimut: (((azSur / RAD + 180) % 360) + 360) % 360, altura: altura / RAD };
}

/** Hacia dónde mira la fachada de la unidad: la del frente, o la opuesta si es del contrafrente. Sin cargar, el norte. */
export function haciaFachada(frenteAzimut: number | null, frente: boolean): number {
  return ((frenteAzimut ?? 0) + (frente ? 0 : 180)) % 360;
}

/** Vector unitario hacia el sol en la escena (la fachada mira a +z; a su derecha, −x). */
export function direccionSol(sol: PosicionSol, fachada: number): [number, number, number] {
  const d = (sol.azimut - fachada) * RAD;
  const h = sol.altura * RAD;
  return [-Math.sin(d) * Math.cos(h), Math.sin(h), Math.cos(d) * Math.cos(h)];
}

export type Momento = "noche" | "atardecer" | "dia";

export function momentoDelDia(altura: number): Momento {
  if (altura < 0) return "noche";
  return altura < 10 ? "atardecer" : "dia";
}

export function aEscena(x: number, y: number): { x: number; z: number } {
  return { x, z: y === 0 ? 0 : -y };
}

const ORDEN: Record<string, number> = { living: 0, monoambiente: 0, local: 0, oficina: 0, dormitorio: 1, cocina: 2, banio: 3, balcon: 4 };

export interface Destino {
  nombre: string;
  tipo: string;
  x: number;
  z: number;
}

/** Los ambientes a los que se puede ir (sin pasillos): el principal primero, el balcón al final. */
export function ambientesParaIr(ambientes: (RectPlanta & { tipo: string; nombre: string })[]): Destino[] {
  return ambientes
    .filter((a) => a.tipo in ORDEN)
    .map((a, i) => ({ a, i }))
    .sort((p, q) => ORDEN[p.a.tipo]! - ORDEN[q.a.tipo]! || p.i - q.i)
    .map(({ a }) => ({ nombre: a.nombre, tipo: a.tipo, ...aEscena((a.x0 + a.x1) / 2, (a.y0 + a.y1) / 2) }));
}

/** Dónde pararse para mostrar un ambiente: hacia el fondo, mirando a la fachada (en el balcón, hacia afuera). */
export function mirarDesde(a: RectPlanta & { tipo: string }): { x: number; z: number; mirarX: number; mirarZ: number } {
  const x = (a.x0 + a.x1) / 2;
  if (a.tipo === "balcon") return { x, z: aEscena(0, (a.y0 + a.y1) / 2).z, mirarX: x, mirarZ: 20 };
  return { x, z: aEscena(0, a.y0 + 0.75 * (a.y1 - a.y0)).z, mirarX: x, mirarZ: 0 };
}

const MARGEN = 0.3; // no pegarse a los muros ni a la baranda

/** El punto de la unidad más cercano a (x, z), a un margen de los bordes: caminar nunca la deja. */
export function recortarAlPiso(pisos: RectPlanta[], x: number, z: number): { x: number; z: number } {
  const y = -z;
  let mejor: { x: number; y: number; d: number } | null = null;
  for (const r of pisos) {
    const cx = Math.min(Math.max(x, r.x0 + MARGEN), r.x1 - MARGEN);
    const cy = Math.min(Math.max(y, r.y0 + MARGEN), r.y1 - MARGEN);
    const d = (cx - x) ** 2 + (cy - y) ** 2;
    if (!mejor || d < mejor.d) mejor = { x: cx, y: cy, d };
  }
  if (!mejor) return { x, z };
  return aEscena(Math.round(mejor.x * 1000) / 1000, Math.round(mejor.y * 1000) / 1000);
}

export type AcabadoPiso = "roble" | "nogal" | "porcelanato" | "microcemento";

export const ACABADOS_PISO: { valor: AcabadoPiso; etiqueta: string }[] = [
  { valor: "roble", etiqueta: "Madera roble claro" },
  { valor: "nogal", etiqueta: "Madera nogal" },
  { valor: "porcelanato", etiqueta: "Porcelanato gris claro" },
  { valor: "microcemento", etiqueta: "Microcemento" },
];

export const COLORES_PARED: { valor: string; etiqueta: string }[] = [
  { valor: "#f3f0ea", etiqueta: "Blanco tiza" },
  { valor: "#e6dccb", etiqueta: "Arena" },
  { valor: "#cfcdc6", etiqueta: "Gris piedra" },
  { valor: "#c9d2c2", etiqueta: "Verde salvia" },
];

/** Una línea para debajo del sol: la estación, la hora y qué se ve. */
export function resumenSol(estacion: Estacion, hora: number, sol: PosicionSol): string {
  const nombre = ESTACIONES.find((e) => e.valor === estacion)?.etiqueta.split(" (")[0] ?? "";
  const momento = momentoDelDia(sol.altura);
  const texto = momento === "noche" ? "de noche, con las luces prendidas" : momento === "atardecer" ? "el sol está bajo" : `el sol está a ${Math.round(sol.altura)}° de altura`;
  return `${nombre}, ${etiquetaHora(hora)}: ${texto}`;
}

/** La estación de una fecha (hemisferio sur), para que el recorrido arranque con la luz de estos días. */
export function estacionDe(fecha: Date): Estacion {
  const mes = fecha.getMonth();
  if (mes === 11 || mes <= 1) return "verano";
  if (mes <= 4) return "otonio";
  if (mes <= 7) return "invierno";
  return "primavera";
}

/** Fotos 360° (Blender): el nombre del archivo dice el ambiente ("dormitorio-principal.jpg" → "Dormitorio principal"). */
export function nombreFoto360(nombre: string): string {
  const base = nombre.replace(/\.[a-z0-9]+$/i, "").replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
  return base ? base[0]!.toUpperCase() + base.slice(1) : "Foto 360°";
}

/** La foto achicada (2:1) hasta lo que acepta la placa del celular (`maxTextureSize`; 4096 en los viejos). */
export function medidaTextura(ancho: number, alto: number, maximo: number): { ancho: number; alto: number } {
  if (ancho <= maximo) return { ancho, alto };
  return { ancho: maximo, alto: Math.round((alto * maximo) / ancho) };
}
